/***
 * DASHBOARD-HEADER
 * Header for every dashboard page => container title, filters and navigation
 */
import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import PageHeader from "@/components/page-header"
import { Survey, SurveyState } from "@/redux/survey.types"
import { Filter, FilterState } from "@/redux/filter.types"
import { faBookOpen, faCog, faExchangeAlt, faThumbsUp } from "@fortawesome/free-solid-svg-icons"
import DashboardFiltersWidget from "./dashboard-filters.widget"
import Link from "@/components/link"
import i18n from "@/translate/i18n"
import { useContext, useEffect, useRef, useState } from "react"
import DashboardTipsHelp from "@/help/dashboard-tips.help"
import { HelpGuideContext } from "@/components/help-guide.context"
import { Session } from "@/redux/_session.types"
import Dropdown from "@/components/dropdown"
import { store } from "@/index"
import { surveyActivate, surveyEdit, surveyUpdateTemplateOptions } from "@/redux/survey.actions"
import GetSurveyResultsModal from "@/modals/get-survey-results.modal"

interface StateProps extends WithTranslation {
  _session: Session
  filter: FilterState
  survey: SurveyState
}

interface OwnProps {
  isPreview?: boolean
  hideFilters?: boolean
  onOptions?: Function | undefined
  showSurveySelector?: boolean
  title?: string
}

const HELP_DASHBOARD: string = "HELP_DASHBOARD"
const MODAL_GET_RESULTS: string = "MODAL_GET_RESULTS"

type Props = StateProps & OwnProps

function DashboardHeaderWidget(props: Props) {
  const { t } = props

  const [currentHelp, setCurrentHelp] = useState<null | string>(null)

  //Guide of the page (written in Strapi) : the link is displayed only if the guide is published and meant for the role
  const helpGuide = useContext(HelpGuideContext)

  const [currentModal, setCurrentModal] = useState<null | string>(null)

  //Filters stuck to the top of the page when scrolling
  const [isStuck, setIsStuck] = useState<boolean>(false)

  //All the filters are displayed
  const [isShowingAll, setIsShowingAll] = useState<boolean>(false)

  const [pageWidth, setPageWidth] = useState<number>(0)

  const filtersRef = useRef<HTMLDivElement>(null)

  //Filters are displayed in a band on the full width of the page
  //Not in the preview : the band is not sticky there
  const isSticked = !props.isPreview && (isStuck || isShowingAll)
  const sideWidth = isSticked ? (pageWidth - 1000) / 2 : 0

  //Detect when the filters stick to the top of the page
  useEffect(() => {
    const page = document.getElementById("page-navigation-scrollable")
    if (props.isPreview || props.hideFilters || !page) return

    function update() {
      if (!filtersRef.current || !page) return
      setIsStuck(filtersRef.current.getBoundingClientRect().top <= page.getBoundingClientRect().top)
      setPageWidth(page.clientWidth)
    }

    update()
    page.addEventListener("scroll", update)
    window.addEventListener("resize", update)
    return () => {
      page.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [props.isPreview, props.hideFilters])

  //Click on the survey
  function selectSurvey(survey: Survey) {
    store.dispatch(surveyActivate(survey))
    setCurrentModal(MODAL_GET_RESULTS)
  }

  async function switchRpsDisplay() {
    const newOptions = {
      ...props.survey.active.template.options,
      isGollac: !props.survey.active.template.options.isGollac
    }

    store.dispatch(surveyEdit("template", { ...props.survey.active.template, options: newOptions }))
    const response: any = await store.dispatch(
      surveyUpdateTemplateOptions(props.survey.active.id, { isGollac: newOptions.isGollac })
    )
    //No reload : it dropped the active survey. The template of the survey is already updated in the store
    if (response.error) {
      store.dispatch(surveyEdit("template", { ...props.survey.active.template, options: props.survey.active.template.options }))
    }
  }

  //Fragment so the filters can stick to the top of the whole page, not only of the header
  return (
    <>
      {currentHelp === HELP_DASHBOARD && <DashboardTipsHelp onClose={() => setCurrentHelp(null)} />}

      {currentModal === MODAL_GET_RESULTS && <GetSurveyResultsModal onClose={() => setCurrentModal(null)} />}

      {props.isPreview ? (
        <h2 style={{ marginBottom: "32px" }}>{props.title}</h2>
      ) : (
        <PageHeader
          title={props.title ? t(props.title) : props.survey.active.name}
          subtitle={t("dashboard_overview_title", { name: props.survey.active.name })}>
          {/* Cancel the right margin of the links to align them with the filters */}
          <div style={{ marginRight: -10 }}>
            {props.onOptions &&
              (props._session.userRole === "ADMIN" || props._session.accountOptions.allowObserverSettings) && (
                <Link icon={faCog} onClick={props.onOptions}>
                  {t("utils_edit_display")}
                </Link>
              )}

            {props._session.connectedAsSupervisor && window.location.pathname === "/dashboard/rps" && (
              <Link icon={faExchangeAlt} onClick={switchRpsDisplay}>
                {props.survey.active.template.options.isGollac ? "Affichage Karasek" : "Affichage Gollac"}
              </Link>
            )}

            {helpGuide.isAvailable && (
              <Link icon={faBookOpen} onClick={() => helpGuide.open()}>
                Guide utilisateur
              </Link>
            )}

            {i18n.language === "fr" && props._session.modules.survey.learning && (
              <Link icon={faThumbsUp} onClick={() => setCurrentHelp(HELP_DASHBOARD)}>
                Afficher les conseils
              </Link>
            )}
          </div>
        </PageHeader>
      )}

      <div>
        {props.survey.list.length > 1 && props.showSurveySelector && (
          <Dropdown
            active={props.survey.active.id}
            displayField="name"
            list={props.survey.list}
            title={t("survey")}
            onSelect={(survey: Survey) => selectSurvey(survey)}
            status={props.survey.status}
            value={props.survey.active.name}
          />
        )}
      </div>

      <div
        ref={filtersRef}
        className="flex lightwhite"
        style={{
          width: props.isPreview ? "100%" : 1000, //The preview of the project is narrower than the page : the band follows its container
          //Padding of the band compensated by the margins to keep the same height
          margin: isSticked ? `-10px ${-sideWidth}px 10px` : "0 0 20px",
          padding: isSticked ? `10px ${sideWidth}px` : 0,
          boxShadow: isSticked ? "0px 0px 4px #d4d4d4" : "0px 0px 0px #d4d4d400",
          transition: "margin 0.2s, padding 0.2s, box-shadow 0.2s",
          position: props.isPreview || props.hideFilters ? undefined : "sticky",
          //Cancel the top padding of the scrollable page
          top: -20,
          zIndex: 5
        }}>
        {!props.hideFilters && (
          <DashboardFiltersWidget
            currentFilters={props.filter.list.filter((x: Filter) => !x.default)}
            currentSurvey={props.survey.active}
            hideOverflow
            isPreview={props.isPreview}
            activateDnd
            isSticked={isSticked}
            onShowAll={setIsShowingAll}
          />
        )}
      </div>
    </>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  filter: state.filter,
  survey: state.survey
})

export default connect(mapStateToProps)(withTranslation()(DashboardHeaderWidget))
